import {releaseDecision, dragOffset, velocityTracker, drawerGesture, DRAG} from './sheet-state.mjs';
import {t} from './i18n.mjs';
// The main menu is its own layer above everything: a drawer from the left edge with a scrim, independent of the one-sheet-at-a-time
// machine (sheets.mjs). Detail and filter keep their state underneath. This module owns open/closed, scrim, focus (trap and return),
// Escape and the swipe; which page shows and the history entries are menu.mjs. Events: `drawerchange` {open}, cancelable `menuescape`.
const FOCUSABLE = 'button, input, select, a[href], summary';
const usable = e => !e.disabled && !e.closest('[hidden]') && e.getClientRects?.().length !== 0;

export function installDrawer({doc = document, win = globalThis,
  reducedMotion = () => win.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false} = {}) {
  const $ = id => doc.getElementById(id), el = $('menu'), scrim = $('menu-scrim'), burger = $('menu-button'), root = doc.documentElement;
  let open = false, token = 0, previousFocus = null;

  // Wait for the transform transition, with a timer in case transitionend never fires.
  function afterTransition(done) {
    if (reducedMotion()) { done(); return; }
    let finished = false;
    const finish = event => {
      if (finished || (event && (event.target !== el || event.propertyName !== 'transform'))) return;
      finished = true; el.removeEventListener('transitionend', finish); clearTimeout(timer); done();
    };
    const timer = setTimeout(finish, 450);
    el.addEventListener('transitionend', finish);
  }
  // The burger names its next action.
  function name(expanded) {
    const key = expanded ? 'menuClose' : 'menuOpen';
    burger.setAttribute('aria-expanded', String(expanded));
    burger.setAttribute('data-i18n-aria', key); burger.setAttribute('aria-label', t(key));
    burger.setAttribute('data-i18n-title', key); burger.setAttribute('title', t(key));
  }
  const announce = () => doc.dispatchEvent?.(new CustomEvent('drawerchange', {detail: {open}}));
  function show() {
    if (open) return;
    open = true; ++token;
    previousFocus = doc.activeElement;
    el.hidden = false; scrim.hidden = false; name(true);
    root?.toggleAttribute('data-drawer-open', true);
    void el.offsetHeight;
    el.classList.add('is-open'); scrim.classList.add('is-open');
    // The first control that is really there (the back button is hidden on the list page).
    ([...el.querySelectorAll(FOCUSABLE)].find(usable) ?? el).focus({preventScroll: true});
    announce();
  }
  function hide() {
    if (!open) return;
    open = false; const mine = ++token;
    el.classList.remove('is-open', 'is-dragging'); el.style.transform = ''; scrim.classList.remove('is-open'); scrim.style.opacity = '';
    name(false); root?.toggleAttribute('data-drawer-open', false);
    announce();
    afterTransition(() => { if (mine === token) { el.hidden = true; scrim.hidden = true; } });
    previousFocus?.focus?.({preventScroll: true});
  }
  const toggle = () => { if (open) hide(); else show(); };

  scrim.addEventListener('click', hide);
  $('menu-close')?.addEventListener('click', hide);
  doc.addEventListener('keydown', event => {
    if (!open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      // The menu takes Escape for an inner level (its sub-pages) by cancelling this event.
      if (doc.dispatchEvent?.(new CustomEvent('menuescape', {cancelable: true})) !== false) hide();
    }
    if (event.key === 'Tab') {
      const items = [...el.querySelectorAll(FOCUSABLE)].filter(usable), first = items[0], last = items.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });

  // Swipe to the left closes: the drawer follows the finger, release decides by distance or flick speed (same rules as the bottom sheets).
  const tracker = velocityTracker();
  let origin = 0;
  const begin = (x, time) => { origin = -x; tracker.reset(); tracker.add(origin, time); };
  const follow = (x, time) => {
    tracker.add(-x, time); const d = -x - origin;
    el.classList.add('is-dragging'); el.style.transform = `translateX(${-dragOffset(d)}px)`;
    scrim.style.opacity = String(Math.max(0, 1 - Math.max(0, d) / el.offsetWidth));
  };
  const release = (x, cancelled) => {
    scrim.style.opacity = ''; el.classList.remove('is-dragging');
    if (!cancelled && releaseDecision(-x - origin, tracker.velocity(), el.offsetWidth) === 'close') hide(); else el.style.transform = '';
  };
  const header = el.querySelector('.sheet-header'), body = el.querySelector('.sheet-body');
  let start = null, dragging = false, pointer = null;
  header.addEventListener('pointerdown', event => {
    if (event.button > 0 || event.target.closest('button, a')) return;
    start = event.clientX; pointer = event.pointerId; dragging = false; begin(event.clientX, event.timeStamp);
    header.setPointerCapture?.(pointer);
  });
  header.addEventListener('pointermove', event => {
    if (start === null || event.pointerId !== pointer) return;
    if (!dragging && Math.abs(event.clientX - start) < DRAG.slop) { tracker.add(-event.clientX, event.timeStamp); return; }
    dragging = true; follow(event.clientX, event.timeStamp);
  });
  const end = event => {
    if (start === null || event.pointerId !== pointer) return;
    const was = dragging; start = null; dragging = false; header.releasePointerCapture?.(pointer);
    if (was) release(event.clientX, event.type !== 'pointerup');
  };
  header.addEventListener('pointerup', end); header.addEventListener('pointercancel', end);
  // Content: touch events, because the browser would take over a pointer gesture for scrolling.
  let touch = null;
  body.addEventListener('touchstart', event => {
    const p = event.touches[0];
    touch = event.touches.length === 1 ? {x: p.clientX, y: p.clientY, mode: 'pending'} : null;
  }, {passive: true});
  body.addEventListener('touchmove', event => {
    if (!touch || touch.mode === 'scroll') return;
    const p = event.touches[0];
    if (touch.mode === 'pending') {
      const mode = drawerGesture({dx: p.clientX - touch.x, dy: p.clientY - touch.y});
      if (mode === 'pending') return;
      // Once native scrolling runs, moves are no longer cancelable; then leave it to the browser.
      if (mode === 'scroll' || event.cancelable === false) { touch.mode = 'scroll'; return; }
      touch.mode = 'sheet'; begin(p.clientX, event.timeStamp);
    }
    event.preventDefault(); follow(p.clientX, event.timeStamp);
  }, {passive: false});
  const finish = event => {
    if (touch?.mode === 'sheet') release(event.changedTouches[0].clientX, event.type === 'touchcancel');
    touch = null;
  };
  body.addEventListener('touchend', finish); body.addEventListener('touchcancel', finish);

  name(false);
  return {open: show, close: hide, toggle, get isOpen() { return open; }};
}
